/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The three-part test outcome (#895 · R3).
 *
 * Reached · Answered · Filled, in that order, each with its own state. It
 * replaces a single pass/fail line that could not distinguish "the host never
 * answered" from "the exchange refused" from "the exchange answered and had
 * nothing to sell" — three facts an operator has to act on differently.
 *
 * Kept separate from `QuickTestPanel` because the saved-source readiness panel
 * (R9, increment 5) renders the same strip from the persisted last-test result.
 */

import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import type { ObservationState, TestObservation, TestOutcome } from '../../utils/test-diagnosis';

/** Ink per observation state. `none` is an observed negative, not an error. */
const STATE_INK: Record<ObservationState, string> = {
  ok: 'success.ink',
  fail: 'error.ink',
  none: 'warning.ink',
  incomplete: 'warning.ink',
  unobserved: 'fg.muted',
};

const STATE_DOT: Record<ObservationState, string> = {
  ok: 'success.solid',
  fail: 'error.solid',
  none: 'warning.solid',
  incomplete: 'warning.solid',
  unobserved: 'transparent',
};

const TONE_TINT = {
  success: 'success.tint',
  warning: 'warning.tint',
  error: 'error.tint',
} as const;

const TONE_BORDER = {
  success: 'success.border',
  warning: 'warning.border',
  error: 'error.border',
} as const;

/**
 * A filled dot for an observation that was made, a hollow one for an
 * observation the test never got far enough to make.
 */
const Dot: React.FC<{ state: ObservationState }> = ({ state }) => (
  <Box
    w="8px"
    h="8px"
    borderRadius="full"
    flexShrink={0}
    bg={STATE_DOT[state]}
    borderWidth={state === 'unobserved' ? '1.5px' : '0'}
    borderStyle="solid"
    borderColor="fg.muted"
  />
);

const Observation: React.FC<{ observation: TestObservation }> = ({ observation }) => (
  <Flex align="baseline" gap={2} minW={0}>
    <Flex align="center" h="1.2em">
      <Dot state={observation.state} />
    </Flex>
    <Text fontSize="xs" color={STATE_INK[observation.state]} minW={0}>
      <Text as="span" fontWeight="700">
        {observation.label}
      </Text>{' '}
      <Text as="span" color="fg.muted">
        {observation.detail}
      </Text>
    </Text>
  </Flex>
);

interface TestOutcomeStripProps {
  outcome: TestOutcome;
}

export const TestOutcomeStrip: React.FC<TestOutcomeStripProps> = ({ outcome }) => (
  <Box
    p={3}
    bg={TONE_TINT[outcome.summary.tone]}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={TONE_BORDER[outcome.summary.tone]}
    borderRadius="6px"
  >
    <Flex direction="column" gap={1.5}>
      <Observation observation={outcome.reached} />
      <Observation observation={outcome.answered} />
      <Observation observation={outcome.filled} />
    </Flex>
  </Box>
);
